import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer, ToolbarProps, View, SlotInfo } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/ru'; // Import required moment locales
import { Typography, Card, Spin, Alert, Button, Space, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { streamService } from '../../services/streamService';
import { Stream, CalendarEvent } from '../../types/stream';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './BigCalendarDarkMode.css'; // Import dark mode styles

const { Title } = Typography;

// Define status colors for dark theme
const STATUS_COLORS = {
  planned: '#1890ff', // blue
  live: '#ff4d4f',    // red
  ended: '#8c8c8c'    // gray
};

// Set up the localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Custom Toolbar component to ensure button clickability
const CustomToolbar: React.FC<ToolbarProps<CalendarEvent, object>> = (props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  
  const handleView = (view: View) => {
    props.onView(view);
  };

  return (
    <div className="rbc-toolbar" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
      <span className="rbc-btn-group" style={{ display: 'flex', gap: '5px' }}>
        <Button type="default" onClick={() => props.onNavigate('TODAY')} className="ant-btn">
          {t('streams.list.toolbar.today', 'Today')}
        </Button>
        <Button type="default" onClick={() => props.onNavigate('PREV')} className="ant-btn">
          &lt;
        </Button>
        <Button type="default" onClick={() => props.onNavigate('NEXT')} className="ant-btn">
          &gt;
        </Button>
      </span>

      <span className="rbc-toolbar-label" style={{ color: token.colorTextBase }}>
        {props.label}
      </span>

      <Space size="small" className="rbc-btn-group" style={{ display: 'flex', flexDirection: 'row' }}>
        <Button 
          type={props.view === 'month' ? 'primary' : 'default'} 
          onClick={() => handleView('month')}
          className={props.view === 'month' ? 'ant-btn-primary' : 'ant-btn'}
        >
          {t('streams.list.view.month')}
        </Button>
        <Button 
          type={props.view === 'week' ? 'primary' : 'default'} 
          onClick={() => handleView('week')}
          className={props.view === 'week' ? 'ant-btn-primary' : 'ant-btn'}
        >
          {t('streams.list.view.week')}
        </Button>
        <Button 
          type={props.view === 'day' ? 'primary' : 'default'} 
          onClick={() => handleView('day')}
          className={props.view === 'day' ? 'ant-btn-primary' : 'ant-btn'}
        >
          {t('streams.list.view.day')}
        </Button>
        <Button 
          type={props.view === 'agenda' ? 'primary' : 'default'} 
          onClick={() => handleView('agenda')}
          className={props.view === 'agenda' ? 'ant-btn-primary' : 'ant-btn'}
        >
          {t('streams.list.view.agenda')}
        </Button>
      </Space>
    </div>
  );
};

// Function to determine if dates are on the same day
const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const StreamsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize state from URL params or use defaults
  const getInitialView = (): View => {
    const viewParam = searchParams.get('view');
    if (viewParam && ['day', 'week', 'month', 'agenda'].includes(viewParam)) {
      return viewParam as View;
    }
    return 'day';
  };
  
  const getInitialDate = (): Date => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsedDate = new Date(dateParam);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    return new Date();
  };
  
  const [currentView, setCurrentView] = useState<View>(getInitialView());
  const [currentDate, setCurrentDate] = useState<Date>(getInitialDate());
  const [agendaDateRange, setAgendaDateRange] = useState<{start: Date, end: Date} | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { token } = theme.useToken();

  // Set moment locale based on current language
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  // Sync state with URL params when they change (e.g., browser back/forward)
  useEffect(() => {
    const viewParam = searchParams.get('view');
    const dateParam = searchParams.get('date');
    
    if (viewParam && ['day', 'week', 'month', 'agenda'].includes(viewParam)) {
      const newView = viewParam as View;
      if (newView !== currentView) {
        setCurrentView(newView);
      }
    }
    
    if (dateParam) {
      const parsedDate = new Date(dateParam);
      if (!isNaN(parsedDate.getTime()) && parsedDate.getTime() !== currentDate.getTime()) {
        setCurrentDate(parsedDate);
      }
    }
  }, [searchParams]);

  // Set initial URL params if they don't exist
  useEffect(() => {
    if (!searchParams.has('view') || !searchParams.has('date')) {
      setSearchParams({
        view: currentView,
        date: currentDate.toISOString()
      }, { replace: true });
    }
  }, []);

  // Calculate date range based on current view and date
  const getDateRange = useCallback((view: View, date: Date) => {
    const start = new Date(date);
    const end = new Date(date);
    
    switch (view) {
      case 'day':
        // Start of day to end of day
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Start of week to end of week
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        end.setDate(diff + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        // Start of month to end of month
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(date.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'agenda':
        // For agenda view, show 30 days
        start.setDate(date.getDate() - 15);
        start.setHours(0, 0, 0, 0);
        end.setDate(date.getDate() + 15);
        end.setHours(23, 59, 59, 999);
        break;
    }
    
    return { start, end };
  }, []);

  // Fetch streams based on date range with expanded range for overnight events
  const fetchStreams = useCallback(async (view: View, date: Date) => {
    try {
      setLoading(true);
      
      // Get date range for the current view
      const { start, end } = getDateRange(view, date);
      
      // Expand the range by one day in each direction to catch overnight events
      const expandedStart = new Date(start);
      expandedStart.setDate(expandedStart.getDate() - 1);
      
      const expandedEnd = new Date(end);
      expandedEnd.setDate(expandedEnd.getDate() + 1);
      
      // Fetch streams using the expanded date range
      const streams = await streamService.getStreamsByDateRange(expandedStart, expandedEnd);
      
      // Process the streams
      let calendarEvents: CalendarEvent[] = [];
      
      streams.forEach((stream: Stream) => {
        const startDate = new Date(stream.start_datetime);
        const endDate = new Date(stream.end_datetime);
        
        // Validate end date is after start date
        if (endDate <= startDate) {
          const validEndDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
          calendarEvents.push({
            id: Number(stream.id),
            title: stream.title,
            start: startDate,
            end: validEndDate,
            resource: stream
          });
          return;
        }
        
        // Check if the event spans different days
        if (!isSameDay(startDate, endDate)) {
          // First part: from start time to end of the first day
          const firstDayEnd = new Date(startDate);
          firstDayEnd.setHours(23, 59, 59, 999);
          
          // Format times for display
          const startTimeStr = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const endTimeStr = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          
          // Add first day part
          calendarEvents.push({
            id: Number(stream.id),
            title: `${stream.title} (${startTimeStr} - ${endTimeStr})`,
            start: startDate,
            end: firstDayEnd,
            resource: stream
          });
          
          // Second part: from start of second day to end time
          const secondDayStart = new Date(endDate);
          secondDayStart.setHours(0, 0, 0, 0);
          
          // Add second day part
          calendarEvents.push({
            id: Number(stream.id),
            title: `${stream.title} (${startTimeStr} - ${endTimeStr})`,
            start: secondDayStart,
            end: endDate,
            resource: stream
          });
        } else {
          // Single day event
          calendarEvents.push({
            id: Number(stream.id),
            title: stream.title,
            start: startDate,
            end: endDate,
            resource: stream
          });
        }
      });
      
      setEvents(calendarEvents);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch streams:', err);
      setError(t('streams.notifications.error', { message: 'Failed to load streams' }));
    } finally {
      setLoading(false);
    }
  }, [getDateRange, t, isSameDay]);

  // Fetch streams when view or date changes
  useEffect(() => {
    fetchStreams(currentView, currentDate);
  }, [currentView, currentDate, fetchStreams]);

  // Function to scroll to current time indicator
  const scrollToCurrentTime = useCallback(() => {
    const calendarEl = document.querySelector('.rbc-time-view .rbc-time-content');
    if (calendarEl) {
      // Allow a small delay for the calendar to render fully
      setTimeout(() => {
        const currentTimeIndicator = document.querySelector('.rbc-current-time-indicator');
        if (currentTimeIndicator) {
          currentTimeIndicator.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, []);

  // Add effect to scroll to current time in day view when switching views
  useEffect(() => {
    if (currentView === 'day') {
      scrollToCurrentTime();
    }
  }, [currentView, scrollToCurrentTime]);
  
  // Add effect to scroll to current time when component first mounts (if in day view)
  useEffect(() => {
    if (currentView === 'day' && !loading) {
      scrollToCurrentTime();
    }
  }, [loading, currentView, scrollToCurrentTime]);

  // Capture the real date range for agenda view after navigating
  useEffect(() => {
    if (currentView === 'agenda') {
      setTimeout(() => {
        // Get the date range from the header label
        const headerLabel = document.querySelector('.rbc-toolbar-label');
        if (headerLabel && headerLabel.textContent) {
          const dateText = headerLabel.textContent;
          const matches = dateText.match(/(\d{2}\/\d{2}\/\d{4}) – (\d{2}\/\d{2}\/\d{4})/);
          
          if (matches && matches.length === 3) {
            const startDate = new Date(matches[1]);
            const endDate = new Date(matches[2]);
            
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              setAgendaDateRange({ start: startDate, end: endDate });
            }
          }
        }
      }, 100);
    }
  }, [currentView, currentDate]);

  // Force update of time indicator at regular intervals
  useEffect(() => {
    // Function to force redraw of time indicator
    const forceUpdateTimeIndicator = () => {
      const indicator = document.querySelector('.rbc-current-time-indicator');
      if (indicator) {
        // Get the container to calculate positioning
        const timeContent = document.querySelector('.rbc-time-content');
        if (timeContent) {
          // Get current time
          const now = new Date();
          const totalMinutes = now.getHours() * 60 + now.getMinutes();
          const percentOfDay = totalMinutes / (24 * 60);
          
          // Adjust the indicator position
          const contentHeight = timeContent.clientHeight;
          const top = contentHeight * percentOfDay;
          
          // Apply the position
          (indicator as HTMLElement).style.top = `${top}px`;
        }
      }
    };
    
    // Initial force update
    if (currentView === 'day' || currentView === 'week') {
      setTimeout(forceUpdateTimeIndicator, 500);
      
      // Set up interval to update every minute
      timerRef.current = setInterval(forceUpdateTimeIndicator, 60000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentView, currentDate]);

  const handleCreateStream = () => {
    navigate('/streams/create');
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    // Navigate to edit page while preserving the current calendar state in URL
    navigate(`/streams/${event.id}/edit`);
  };

  const handleViewChange = (view: View) => {
    setCurrentView(view);
    // Update URL params
    setSearchParams({
      view: view,
      date: currentDate.toISOString()
    });
  };

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
    // Update URL params
    setSearchParams({
      view: currentView,
      date: date.toISOString()
    });
  };

  // Handle time slot selection
  const handleSelectSlot = (slotInfo: SlotInfo) => {
    console.log('Time slot selected:', slotInfo.start, slotInfo.end);
  };

  // Event style getter based on stream status
  const eventPropGetter = (event: CalendarEvent) => {
    const status = event.resource?.status || 'planned';
    const backgroundColor = STATUS_COLORS[status] || STATUS_COLORS.planned;
    
    return {
      style: {
        backgroundColor,
        borderColor: token.colorBorderSecondary,
        border: `1px solid ${token.colorBorderSecondary}`
      }
    };
  };

  // Add CSS to fix calendar views
  useEffect(() => {
    // Fix to prevent overnight events from being treated as all-day events
    const style = document.createElement('style');
    style.innerHTML = `
      /* Remove all-day section completely */
      .rbc-allday-cell {
        display: none !important;
      }
      .rbc-time-view .rbc-header {
        border-bottom: none !important;
      }
      
      /* Force events to be visible in the correct time slots */
      .rbc-time-view .rbc-event {
        z-index: 4;
      }
      
      /* Show the first day of an event */
      .rbc-event-content {
        white-space: normal !important;
      }
      
      /* Make sure the time indicator is visible over events */
      .rbc-current-time-indicator {
        z-index: 100 !important;
        position: absolute !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        height: 2px !important;
        background-color: #f5222d !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Card title={t('streams.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: STATUS_COLORS.planned, borderRadius: '2px' }}></div>
            <span>{t('streams.form.status.planned')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: STATUS_COLORS.live, borderRadius: '2px' }}></div>
            <span>{t('streams.form.status.live')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: STATUS_COLORS.ended, borderRadius: '2px' }}></div>
            <span>{t('streams.form.status.ended')}</span>
          </div>
        </div>
        <Button type="primary" onClick={handleCreateStream}>
          {t('streams.actions.create')}
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert message={error} type="error" />
      ) : (
        <div style={{ height: 'auto', minHeight: '600px' }}>
          <Calendar
            localizer={localizer}
            culture={i18n.language}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%', minHeight: '600px', position: 'relative' }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable={true}
            view={currentView}
            onView={handleViewChange}
            date={currentDate}
            onNavigate={handleNavigate}
            eventPropGetter={eventPropGetter}
            scrollToTime={new Date()}
            components={{
              toolbar: CustomToolbar
            }}
            defaultView={currentView}
            popup={false}
            dayLayoutAlgorithm="no-overlap"
            showAllEvents={true}
            // Explicitly set maximum number of events to display (forces stacking)
            slotGroupPropGetter={() => ({
              style: { minHeight: '200px' }
            })}
            popupOffset={{ x: 0, y: 5 }}
            className="month-view-auto-height"
            formats={{
              dateFormat: 'D',
              dayFormat: 'ddd D/M',
              monthHeaderFormat: 'MMMM YYYY',
              dayHeaderFormat: 'dddd, MMMM D, YYYY',
              dayRangeHeaderFormat: ({ start, end }) => 
                `${moment(start).format('MMMM D')} - ${moment(end).format('MMMM D, YYYY')}`
            }}
            messages={{
              noEventsInRange: t('streams.list.no_events'),
              allDay: t('streams.list.view.day'),
              previous: '<',
              next: '>',
              today: t('streams.list.toolbar.today', 'Today'),
              month: t('streams.list.view.month'),
              week: t('streams.list.view.week'),
              day: t('streams.list.view.day'),
              agenda: t('streams.list.view.agenda')
            }}
          />
          
          {/* Bottom toolbar duplicating the top toolbar */}
          <div style={{ marginTop: '10px' }}>
            <CustomToolbar
              date={currentDate}
              view={currentView}
              views={['month', 'week', 'day', 'agenda']}
              localizer={localizer}
              onView={handleViewChange}
              onNavigate={(action) => {
                if (action === 'TODAY') {
                  handleNavigate(new Date());
                } else if (action === 'PREV') {
                  const newDate = new Date(currentDate);
                  if (currentView === 'month') {
                    newDate.setMonth(newDate.getMonth() - 1);
                  } else if (currentView === 'week') {
                    newDate.setDate(newDate.getDate() - 7);
                  } else if (currentView === 'day') {
                    newDate.setDate(newDate.getDate() - 1);
                  } else {
                    newDate.setDate(newDate.getDate() - 30);
                  }
                  handleNavigate(newDate);
                } else {
                  const newDate = new Date(currentDate);
                  if (currentView === 'month') {
                    newDate.setMonth(newDate.getMonth() + 1);
                  } else if (currentView === 'week') {
                    newDate.setDate(newDate.getDate() + 7);
                  } else if (currentView === 'day') {
                    newDate.setDate(newDate.getDate() + 1);
                  } else {
                    newDate.setDate(newDate.getDate() + 30);
                  }
                  handleNavigate(newDate);
                }
              }}
              label={
                // Use the same format as the top toolbar based on current view
                currentView === 'day' 
                  ? moment(currentDate).format('dddd, MMMM D, YYYY')
                  : currentView === 'week' 
                    ? (() => {
                        // Get start of week (based on locale)
                        const firstDay = moment(currentDate).startOf('week');
                        // Get end of week
                        const lastDay = moment(firstDay).add(6, 'days');
                        // Format like "май 12 - май 18, 2025"
                        return `${firstDay.format('MMMM D')} - ${lastDay.format('MMMM D, YYYY')}`;
                      })()
                    : currentView === 'agenda' && agendaDateRange
                      ? `${moment(agendaDateRange.start).format('MM/DD/YYYY')} - ${moment(agendaDateRange.end).format('MM/DD/YYYY')}`
                      : moment(currentDate).format('MMMM YYYY') // month view
              }
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default StreamsPage; 